<template>
  <b-modal
    id="incident-report-modal"
    :title="readOnly ? 'View Incident Report (Read Only)' : 'Incident Report'"
    :no-close-on-backdrop="true"
    :no-close-on-esc="true"
    centered
    @close="handleBeforeClose"
    @cancel="handleBeforeClose"
  >
    <loading-screen :is-loading="isSaving"></loading-screen>

    <div class="toolbar">
      <div class="container-fluid px-3 d-flex">
        <div class="flex-grow-1">
          <div class="btn-group mr-2">
            <button
              type="button"
              class="btn"
              :class="{
                'btn-itg-dark': currentTabIndex === 0,
                'btn-itg': currentTabIndex !== 0,
              }"
              @click="setTab(0)"
            >
              <i class="fa fa-info-circle mr-2"></i>
              Basic Info
            </button>
            <button
              type="button"
              class="btn"
              :class="{
                'btn-itg-dark': currentTabIndex === 1,
                'btn-itg': currentTabIndex !== 1,
              }"
              @click="setTab(1)"
            >
              <i class="fa fa-building mr-2"></i>
              Travel Agency
            </button>
            <button
              type="button"
              class="btn"
              :class="{
                'btn-itg-dark': currentTabIndex === 2,
                'btn-itg': currentTabIndex !== 2,
              }"
              @click="setTab(2)"
            >
              <i class="fa fa-user mr-2"></i>
              Affected PAX
            </button>
            <button
              type="button"
              class="btn"
              :class="{
                'btn-itg-dark': currentTabIndex === 3,
                'btn-itg': currentTabIndex !== 3,
              }"
              @click="setTab(3)"
            >
              <i class="fa fa-users mr-2"></i>
              Emergency Contacts
            </button>
            <button
              type="button"
              class="btn"
              :class="{
                'btn-itg-dark': currentTabIndex === 4,
                'btn-itg': currentTabIndex !== 4,
              }"
              @click="setTab(4)"
            >
              <i class="fa fa-medkit mr-2"></i>
              Medical & Insurance
            </button>
            <button
              type="button"
              class="btn"
              :class="{
                'btn-itg-dark': currentTabIndex === 5,
                'btn-itg': currentTabIndex !== 5,
              }"
              @click="setTab(5)"
            >
              <i class="fa fa-exclamation-triangle mr-2"></i>
              Incident Details
            </button>
            <button
              type="button"
              class="btn"
              :class="{
                'btn-itg-dark': currentTabIndex === 6,
                'btn-itg': currentTabIndex !== 6,
              }"
              @click="setTab(6)"
            >
              <i class="fa fa-tasks mr-2"></i>
              Actions
            </button>
            <button
              type="button"
              class="btn"
              :class="{
                'btn-itg-dark': currentTabIndex === 7,
                'btn-itg': currentTabIndex !== 7,
              }"
              @click="setTab(7)"
            >
              <i class="fa fa-file-signature mr-2"></i>
              Declaration
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="incident-report-form">
      <div class="pl-4 pr-4 pt-4 mb-0 pb-0" v-if="serverErrors.length > 0">
        <errors :error-list="serverErrors"></errors>
      </div>

      <!-- Basic Information Tab -->
      <div
        v-if="currentTabIndex === 0"
        class="tab-content pl-4 pr-4 pb-4"
        :class="{
          'pt-3': serverErrors.length > 0,
          'pt-4': serverErrors.length === 0,
        }"
      >
        <!-- Incident Report Policy Notice -->
        <div class="row mb-3">
          <div class="col-md-12">
            <div class="policy-accordion" :class="{ 'expanded': policyAccordionOpen }">
              <div class="policy-header" role="button" @click="togglePolicyAccordion">
                <div class="d-flex justify-content-between align-items-center">
                  <h6 class="mb-0">
                    <i class="fa fa-info-circle mr-2"></i>
                    Incident Report Policy
                  </h6>
                  <i class="fa" :class="policyAccordionOpen ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                </div>
              </div>
              <div class="policy-content" v-show="policyAccordionOpen">
                <p class="mb-2">
                  <strong>CX are to complete an incident report when a passenger-related incident occurs.</strong> For example:
                </p>
                <ul class="mb-2">
                  <li>Any accident</li>
                  <li>Any non-minor injury</li>
                  <li>Any non-minor illness that involves referral to a doctor, medical clinic or hospital or use of an ambulance</li>
                </ul>
                <p class="mb-2">
                  <strong>When an incident report is created in TESS, the system automatically shares a link via email with:</strong>
                </p>
                <ul class="mb-2">
                  <li>The TC, their STL, all CXTLs, the CX Manager, Global Head of Operations, and Simon King</li>
                  <li><strong>For IAT bookings:</strong> The Head of IAT is also included</li>
                  <li><strong>For SGT bookings:</strong> The same people are notified plus the TL, Global Field Operations Manager, and Group Tour Programme Manager</li>
                </ul>
                <p class="mb-0">
                  <strong>A PDF version of the form can be downloaded from the TESS incident report ticket and must be emailed manually to our TOL insurers within 5 days of the incident occurring.</strong>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <div class="form-group mb-3">
              <label for="brand">Brand</label>
              <select
                id="brand"
                v-model="incidentReportLocal.brand"
                class="form-control"
                :disabled="readOnly"
              >
                <option value="">Not Specified</option>
                <option value="iat">IAT - Inside Asia Tours</option>
                <option value="ijt">IJT - Inside Japan Tours</option>
              </select>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <div class="form-group mb-3">
              <label for="type">Type *</label>
              <select
                id="type"
                v-model="incidentReportLocal.type"
                class="form-control"
                :disabled="readOnly"
              >
                <option value="">Select type</option>
                <option value="Group">Group</option>
                <option value="FIT">FIT</option>
              </select>
            </div>
          </div>
        </div>

        <div class="row" v-if="incidentReportLocal.type === 'Group' || incidentReportLocal.type === 'FIT'">
          <div class="col-md-12">
            <div class="form-group mb-3">
              <label for="itg_included_activity">ITG Included Activity</label>
              <select
                id="itg_included_activity"
                v-model="incidentReportLocal.itg_included_activity"
                class="form-control"
                :disabled="readOnly"
              >
                <option :value="null">Select option</option>
                <option :value="true">Yes</option>
                <option :value="false">No</option>
              </select>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <div class="form-group mb-3">
              <label for="travel_consultant_id">Travel Consultant</label>
              <v-select
                v-model="incidentReportLocal.travel_consultant_id"
                label="displayName"
                :disabled="readOnly"
                :reduce="agent => agent.id"
                :clearable="true"
                :options="agentsWithDisplayName"
                placeholder="Select travel consultant"
                @input="onTravelConsultantChange"
              ></v-select>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <div class="form-group mb-3">
              <label for="travel_consultant_name">Travel Consultant Name</label>
              <input
                type="text"
                id="travel_consultant_name"
                v-model="incidentReportLocal.travel_consultant_name"
                class="form-control"
                placeholder="Enter travel consultant name"
                :disabled="readOnly"
              />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <div class="form-group mb-3">
              <label for="stl_name">STL Name</label>
              <input
                type="text"
                id="stl_name"
                v-model="incidentReportLocal.stl_name"
                class="form-control"
                placeholder="Enter STL name"
                :disabled="readOnly"
              />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <div class="form-group mb-3">
              <label for="booking_number">Booking Number</label>
              <input
                type="text"
                id="booking_number"
                v-model="incidentReportLocal.booking_number"
                class="form-control"
                placeholder="Enter booking number"
                :disabled="readOnly"
              />
            </div>
          </div>
        </div>

        <div class="row" v-if="incidentReportLocal.type === 'Group'">
          <div class="col-md-6">
            <div class="form-group mb-3">
              <label for="tour_name">Tour Name</label>
              <input
                type="text"
                id="tour_name"
                v-model="incidentReportLocal.tour_name"
                class="form-control"
                placeholder="Enter tour name"
                :disabled="readOnly"
              />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group mb-3">
              <label for="tour_leader">Tour Leader</label>
              <input
                type="text"
                id="tour_leader"
                v-model="incidentReportLocal.tour_leader"
                class="form-control"
                placeholder="Enter tour leader name"
                :disabled="readOnly"
              />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <div class="form-group mb-3">
              <label for="is_solo_pax">Solo PAX? <span v-if="incidentReportLocal.type === 'Group'">(if yes then CX will follow up with the pax independently of the tour leader)</span></label>
              <select
                id="is_solo_pax"
                v-model="incidentReportLocal.is_solo_pax"
                class="form-control"
                :disabled="readOnly"
              >
                <option :value="null">Select option</option>
                <option :value="true">Yes</option>
                <option :value="false">No</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <!-- Travel Agency Tab -->
      <div
        v-if="currentTabIndex === 1"
        class="tab-content pl-4 pr-4 pb-4"
        :class="{
          'pt-3': serverErrors.length > 0,
          'pt-4': serverErrors.length === 0,
        }"
      >
        <div class="row">
          <div class="col-md-12">
            <div class="form-group mb-3">
              <label for="travel_agency_name">Travel Agency Name</label>
              <input
                type="text"
                id="travel_agency_name"
                v-model="incidentReportLocal.travel_agency_name"
                class="form-control"
                placeholder="Enter travel agency name"
                :disabled="readOnly"
              />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <div class="form-group mb-3">
              <label for="travel_agency_contact_name">Travel Agency Contact Name</label>
              <input
                type="text"
                id="travel_agency_contact_name"
                v-model="incidentReportLocal.travel_agency_contact_name"
                class="form-control"
                placeholder="Enter contact name"
                :disabled="readOnly"
              />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <div class="form-group mb-3">
              <label for="travel_agency_contact_email">Travel Agency Contact Email</label>
              <input
                type="email"
                id="travel_agency_contact_email"
                v-model="incidentReportLocal.travel_agency_contact_email"
                class="form-control"
                placeholder="Enter contact email"
                :disabled="readOnly"
              />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group mb-3">
              <label for="travel_agency_contact_phone">Travel Agency Contact Phone</label>
              <input
                type="tel"
                id="travel_agency_contact_phone"
                v-model="incidentReportLocal.travel_agency_contact_phone"
                class="form-control"
                placeholder="Enter contact phone"
                :disabled="readOnly"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Affected PAX Tab -->
      <div
        v-if="currentTabIndex === 2"
        class="tab-content pl-4 pr-4 pb-4"
        :class="{
          'pt-3': serverErrors.length > 0,
          'pt-4': serverErrors.length === 0,
        }"
      >
        <div v-if="incidentReportLocal.incident_report_affected_paxs && incidentReportLocal.incident_report_affected_paxs.length > 0">
          <div
            v-for="(pax, index) in incidentReportLocal.incident_report_affected_paxs"
            :key="index"
            class="row mb-0"
          >
            <div class="col-md-12">
              <div class="card">
                <div 
                  class="card-header d-flex justify-content-between align-items-center"
                  :class="{ 'collapsed-card-header': !isPaxExpanded(index) }"
                >
                  <div class="d-flex align-items-center flex-grow-1">
                    <button
                      type="button"
                      class="btn btn-link p-0 mr-2"
                      @click="togglePaxExpanded(index)"
                      style="min-width: 20px;"
                      :disabled="readOnly"
                    >
                      <i 
                        class="fa"
                        :class="{
                          'fa-chevron-down': isPaxExpanded(index),
                          'fa-chevron-right': !isPaxExpanded(index)
                        }"
                      ></i>
                    </button>
                    <h6 class="mb-0">{{ pax.name ? pax.name :  'Affected PAX ' + (index + 1) }}</h6>
                  </div>
                  <div class="d-flex gap-2 align-items-center">
                    <div class="form-check">
                      <input
                        type="radio"
                        :id="'primary_pax_' + index"
                        :name="'primary_pax'"
                        :value="index"
                        v-model="primaryPaxIndex"
                        class="form-check-input"
                        @change="setPrimaryPax(index)"
                        :disabled="readOnly"
                      />
                      <label class="form-check-label" :for="'primary_pax_' + index">
                        Primary
                      </label>
                    </div>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-danger ml-2"
                      @click="confirmDeletePax(index)"
                      :disabled="readOnly"
                    >
                      <i class="fa fa-trash"></i>
                    </button>
                  </div>
                </div>
                <div 
                  class="card-body"
                  v-show="isPaxExpanded(index)"
                >
                  <div class="row">
                    <div class="col-md-6">
                      <div class="form-group mb-3">
                        <label :for="'pax_name_' + index">Name</label>
                        <input
                          type="text"
                          :id="'pax_name_' + index"
                          v-model="pax.name"
                          class="form-control"
                          placeholder="Enter affected PAX name"
                          :disabled="readOnly"
                        />
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="form-group mb-3">
                        <label :for="'pax_date_of_birth_' + index">Date of Birth</label>
                        <input
                          type="date"
                          :id="'pax_date_of_birth_' + index"
                          v-model="pax.date_of_birth"
                          class="form-control"
                          :disabled="readOnly"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-6">
                      <div class="form-group mb-3">
                        <label :for="'pax_email_' + index">Email</label>
                        <input
                          type="email"
                          :id="'pax_email_' + index"
                          v-model="pax.email"
                          class="form-control"
                          placeholder="Enter email address"
                          :disabled="readOnly"
                        />
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="form-group mb-3">
                        <label :for="'pax_mobile_' + index">Mobile</label>
                        <input
                          type="tel"
                          :id="'pax_mobile_' + index"
                          v-model="pax.mobile"
                          class="form-control"
                          placeholder="Enter mobile number"
                          :disabled="readOnly"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Add Another Affected PAX Button -->
          <div class="row">
            <div class="col-md-12">
              <div class="">
                <button
                  type="button"
                  class="btn btn-itg-dark"
                  @click="addAffectedPax"
                  :disabled="readOnly"
                >
                  <i class="fa fa-plus mr-1"></i>
                  Add Another Affected PAX
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="row">
          <div class="col-md-12">
            <div class="text-center py-4">
              <i class="fa fa-user fa-3x text-muted mb-3"></i>
              <p class="text-muted">No affected PAX added yet.</p>
              <button
                type="button"
                class="btn btn-itg"
                @click="addAffectedPax"
                :disabled="readOnly"
              >
                <i class="fa fa-plus mr-1"></i>
                Add First Affected PAX
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Customer Contacts Tab -->
      <div
        v-if="currentTabIndex === 3"
        class="tab-content pl-4 pr-4 pb-4"
        :class="{
          'pt-3': serverErrors.length > 0,
          'pt-4': serverErrors.length === 0,
        }"
      >

        <div v-if="incidentReportLocal.incident_report_customer_contacts && incidentReportLocal.incident_report_customer_contacts.length > 0">
          <div
            v-for="(contact, index) in incidentReportLocal.incident_report_customer_contacts"
            :key="index"
            class="row mb-0"
          >
            <div class="col-md-12">
              <div class="card">
                <div 
                  class="card-header d-flex justify-content-between align-items-center"
                  :class="{ 'collapsed-card-header': !isContactExpanded(index) }"
                >
                  <div class="d-flex align-items-center flex-grow-1">
                    <button
                      type="button"
                      class="btn btn-link p-0 mr-2"
                      @click="toggleContactExpanded(index)"
                      style="min-width: 20px;"
                      :disabled="readOnly"
                    >
                      <i 
                        class="fa"
                        :class="{
                          'fa-chevron-down': isContactExpanded(index),
                          'fa-chevron-right': !isContactExpanded(index)
                        }"
                      ></i>
                    </button>
                    <h6 class="mb-0">{{ contact.name ? contact.name :  'Contact ' + (index + 1) }}</h6>
                  </div>
                  <div class="d-flex gap-2 align-items-center">
                    <div class="form-check">
                      <input
                        type="radio"
                        :id="'primary_' + index"
                        :name="'primary_contact'"
                        :value="index"
                        v-model="primaryContactIndex"
                        class="form-check-input"
                        @change="setPrimaryContact(index)"
                        :disabled="readOnly"
                      />
                      <label class="form-check-label" :for="'primary_' + index">
                        Primary
                      </label>
                    </div>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-danger ml-2"
                      @click="confirmDeleteContact(index)"
                      :disabled="readOnly"
                    >
                      <i class="fa fa-trash"></i>
                    </button>
                  </div>
                </div>
                <div 
                  class="card-body"
                  v-show="isContactExpanded(index)"
                >
                  <div class="row">
                    <div class="col-md-6">
                      <div class="form-group mb-3">
                        <label :for="'name_' + index">Name</label>
                        <input
                          type="text"
                          :id="'name_' + index"
                          v-model="contact.name"
                          class="form-control"
                          placeholder="Enter contact name"
                          :disabled="readOnly"
                        />
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="form-group mb-3">
                        <label :for="'relationship_' + index">Relationship to Affected PAX</label>
                        <input
                          type="text"
                          :id="'relationship_' + index"
                          v-model="contact.relationship_to_affected_pax"
                          class="form-control"
                          placeholder="e.g., Spouse, Parent, Friend"
                          :disabled="readOnly"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-6">
                      <div class="form-group mb-3">
                        <label :for="'email_' + index">Email</label>
                        <input
                          type="email"
                          :id="'email_' + index"
                          v-model="contact.email"
                          class="form-control"
                          placeholder="Enter email address"
                          :disabled="readOnly"
                        />
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="form-group mb-3">
                        <label :for="'mobile_' + index">Mobile</label>
                        <input
                          type="tel"
                          :id="'mobile_' + index"
                          v-model="contact.mobile"
                          class="form-control"
                          placeholder="Enter mobile number"
                          :disabled="readOnly"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-12">
                      <div class="form-group mb-3">
                        <label :for="'notes_' + index">Notes</label>
                        <textarea
                          :id="'notes_' + index"
                          v-model="contact.notes"
                          class="form-control"
                          rows="2"
                          placeholder="Additional notes about this contact"
                          :disabled="readOnly"
                        ></textarea>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Add Another Contact Button -->
          <div class="row">
            <div class="col-md-12">
              <div class="">
                <button
                  type="button"
                  class="btn btn-itg-dark"
                  @click="addCustomerContact"
                  :disabled="readOnly"
                >
                  <i class="fa fa-plus mr-1"></i>
                  Add Another Contact
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="row">
          <div class="col-md-12">
            <div class="text-center py-4">
              <i class="fa fa-users fa-3x text-muted mb-3"></i>
              <p class="text-muted">No emergency contacts added yet.</p>
              <button
                type="button"
                class="btn btn-itg"
                @click="addCustomerContact"
                :disabled="readOnly"
              >
                <i class="fa fa-plus mr-1"></i>
                Add First Contact
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Medical Information Tab -->
      <div
        v-if="currentTabIndex === 4"
        class="tab-content pl-4 pr-4 pb-4"
        :class="{
          'pt-3': serverErrors.length > 0,
          'pt-4': serverErrors.length === 0,
        }"
      >
        <div class="row">
          <div class="col-md-12">
            <div class="form-group mb-3">
              <label for="doctor_or_hospital_details"
                >Doctor or Hospital Details</label
              >
              <textarea
                id="doctor_or_hospital_details"
                v-model="incidentReportLocal.doctor_or_hospital_details"
                class="form-control"
                rows="3"
                placeholder="Enter doctor or hospital details"
                :disabled="readOnly"
              ></textarea>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <div class="form-group mb-3">
              <label for="travel_insurance_company"
                >Travel Insurance Company</label
              >
              <input
                type="text"
                id="travel_insurance_company"
                v-model="incidentReportLocal.travel_insurance_company"
                class="form-control"
                placeholder="Enter insurance company"
                :disabled="readOnly"
              />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <div class="form-group mb-3">
              <label for="travel_insurance_policy_number"
                >Travel Insurance Policy Number</label
              >
              <input
                type="text"
                id="travel_insurance_policy_number"
                v-model="incidentReportLocal.travel_insurance_policy_number"
                class="form-control"
                placeholder="Enter policy number"
                :disabled="readOnly"
              />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <div class="form-group mb-3">
              <label for="travel_insurance_emergency_phone_number"
                >Travel Insurance Emergency Phone Number</label
              >
              <input
                type="tel"
                id="travel_insurance_emergency_phone_number"
                v-model="
                  incidentReportLocal.travel_insurance_emergency_phone_number
                "
                class="form-control"
                placeholder="Enter emergency phone number"
                :disabled="readOnly"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Incident Details Tab -->
      <div
        v-if="currentTabIndex === 5"
        class="tab-content pl-4 pr-4 pb-4"
        :class="{
          'pt-3': serverErrors.length > 0,
          'pt-4': serverErrors.length === 0,
        }"
      >
        <div class="row">
          <div class="col-md-12">
            <div class="form-group mb-3">
              <label for="date_time_of_incident"
                >Date & Time of Incident</label
              >
              <input
                type="datetime-local"
                id="date_time_of_incident"
                v-model="incidentReportLocal.date_time_of_incident"
                class="form-control"
                :disabled="readOnly"
              />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <div class="form-group mb-3">
              <label for="location_of_incident">Location of Incident</label>
              <input
                type="text"
                id="location_of_incident"
                v-model="incidentReportLocal.location_of_incident"
                class="form-control"
                placeholder="Enter incident location"
                :disabled="readOnly"
              />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <div class="form-group mb-3">
              <label for="was_insider_present">Was Insider Present</label>
              <select
                id="was_insider_present"
                v-model="incidentReportLocal.was_insider_present"
                class="form-control"
                :disabled="readOnly"
              >
                <option :value="null">Select option</option>
                <option :value="true">Yes</option>
                <option :value="false">No</option>
              </select>
            </div>
          </div>
        </div>

        <div class="row" v-if="incidentReportLocal.was_insider_present === true">
          <div class="col-md-12">
            <div class="form-group mb-3">
              <label for="what_did_insider_see_do"
                >What Did Insider See/Do</label
              >
              <textarea
                id="what_did_insider_see_do"
                v-model="incidentReportLocal.what_did_insider_see_do"
                class="form-control"
                rows="3"
                placeholder="Describe what the insider saw or did"
                :disabled="readOnly"
              ></textarea>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <div class="form-group mb-3">
              <label for="description_of_incident"
                >Description of Incident</label
              >
              <textarea
                id="description_of_incident"
                v-model="incidentReportLocal.description_of_incident"
                class="form-control"
                rows="4"
                placeholder="Provide a detailed description of the incident"
                :disabled="readOnly"
              ></textarea>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions & Recommendations Tab -->
      <div
        v-if="currentTabIndex === 6"
        class="tab-content pl-4 pr-4 pb-4"
        :class="{
          'pt-3': serverErrors.length > 0,
          'pt-4': serverErrors.length === 0,
        }"
      >
        <div class="row">
          <div class="col-md-12">
            <div class="form-group mb-3">
              <label for="action_taken">Action Taken</label>
              <textarea
                id="action_taken"
                v-model="incidentReportLocal.action_taken"
                class="form-control"
                rows="3"
                placeholder="Describe actions taken in response to the incident"
                :disabled="readOnly"
              ></textarea>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <div class="form-group mb-3">
              <label for="relevant_safety_briefings"
                >Relevant Safety Briefings</label
              >
              <textarea
                id="relevant_safety_briefings"
                v-model="incidentReportLocal.relevant_safety_briefings"
                class="form-control"
                rows="3"
                placeholder="List any relevant safety briefings"
                :disabled="readOnly"
              ></textarea>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <div class="form-group mb-3">
              <label for="suggested_action_to_avoid_recurrence"
                >Suggested Action to Avoid Recurrence</label
              >
              <textarea
                id="suggested_action_to_avoid_recurrence"
                v-model="
                  incidentReportLocal.suggested_action_to_avoid_recurrence
                "
                class="form-control"
                rows="3"
                placeholder="Suggest actions to prevent similar incidents"
              ></textarea>
            </div>
          </div>
        </div>
      </div>

      <!-- Declaration Tab -->
      <div
        v-if="currentTabIndex === 7"
        class="tab-content pl-4 pr-4 pb-4"
        :class="{
          'pt-3': serverErrors.length > 0,
          'pt-4': serverErrors.length === 0,
        }"
      >
        <div class="row">
          <div class="col-md-12">
            <div class="form-group mb-3">
              <div class="form-check">
                <input
                  type="checkbox"
                  id="declare_facts_true"
                  v-model="incidentReportLocal.declare_facts_true"
                  class="form-check-input"
                  :disabled="readOnly"
                />
                <label class="form-check-label" for="declare_facts_true">
                  I declare that the facts stated in this incident report are
                  true to the best of my knowledge
                </label>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <div class="form-group mb-3">
              <label for="declared_by_name">Declared By Name</label>
              <input
                type="text"
                id="declared_by_name"
                v-model="incidentReportLocal.declared_by_name"
                class="form-control"
                placeholder="Enter your full name"
                :disabled="readOnly"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Footer -->
    <template #modal-footer>
      <div class="w-100 d-flex justify-content-end align-items-center">
        <!-- <button 
          type="button" 
          class="btn btn-secondary" 
          @click="confirmClose"
        >
          Cancel
        </button> -->

        <div class="d-flex gap-2">
          <button 
            type="button" 
            class="btn btn-itg" 
            @click="previousTab"
            :disabled="currentTabIndex === 0"
          >
            <i class="fa fa-chevron-left mr-1"></i>
            Previous
          </button>

          <button
            type="button"
            class="btn btn-itg"
            @click="nextTab"
            :disabled="currentTabIndex === totalTabs - 1"
          >
            Next
            <i class="fa fa-chevron-right ml-1"></i>
          </button>

          <!-- <button 
            type="button" 
            class="btn btn-outline-warning" 
            @click="resetForm"
          >
            Reset
          </button> -->

          <button
            v-if="!readOnly"
            type="button"
            class="btn btn-itg-dark"
            @click="saveIncidentReport"
            :disabled="isSaving"
          >
            <span v-if="isSaving">
              <i class="fa fa-spinner fa-spin mr-1"></i>
              Saving...
            </span>
            <span v-else> Save Incident Report </span>
          </button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import Forms from "../../../../../../mixins/Forms";
import Tickets from "../../../../../../mixins/Tickets";
import axios from "axios";

export default {
  name: "IncidentReportModal",
  mixins: [Forms, Tickets],
  data() {
    return {
      isSaving: false,
      serverErrors: [],
      currentTabIndex: 0,
      totalTabs: 8, // Basic Information, Travel Agency, Affected PAX, Customer Contacts, Medical Information, Incident Details, Actions & Recommendations, Declaration
      primaryContactIndex: null,
      primaryPaxIndex: null,
      expandedContacts: [], // Track which contacts are expanded
      expandedPaxs: [], // Track which affected PAX are expanded
      policyAccordionOpen: false, // Track policy accordion state
      readOnly: false,
      incidentReportLocal: {
        brand: "",
        type: "",
        itg_included_activity: null,
        travel_consultant_name: "",
        travel_consultant_id: null,
        stl_name: "",
        booking_number: "",
        tour_name: "",
        tour_leader: "",
        is_solo_pax: false,
        travel_agency_name: "",
        travel_agency_contact_name: "",
        travel_agency_contact_email: "",
        travel_agency_contact_phone: "",
        incident_report_customer_contacts: [],
        incident_report_affected_paxs: [],
        doctor_or_hospital_details: "",
        travel_insurance_company: "",
        travel_insurance_policy_number: "",
        travel_insurance_emergency_phone_number: "",
        date_time_of_incident: "",
        location_of_incident: "",
        was_insider_present: false,
        what_did_insider_see_do: "",
        description_of_incident: "",
        action_taken: "",
        relevant_safety_briefings: "",
        suggested_action_to_avoid_recurrence: "",
        declare_facts_true: false,
        declared_by_name: "",
      },
    };
  },
  computed: {
    ...mapGetters("ticketViewer", {
      ticket: "ticket",
      incidentReport: "incidentReport",
      agents: "agents",
    }),

    hasUnsavedChanges() {
      if (!this.incidentReport) {
        // If no existing incident report, check if any fields have been filled
        const hasBasicChanges = Object.entries(this.incidentReportLocal).some(([key, value]) => {
          if (key === 'incident_report_customer_contacts') {
            return value && value.length > 0;
          }
          if (typeof value === "boolean") {
            return value === true;
          }
          return value && value.toString().trim() !== "";
        });
        
        return hasBasicChanges || this.primaryContactIndex !== null || this.primaryPaxIndex !== null;
      } else {
        // Compare with existing incident report
        const original = { ...this.incidentReport };
        const current = { ...this.incidentReportLocal };

        // Format datetime for comparison
        if (original.date_time_of_incident) {
          const date = new Date(original.date_time_of_incident);
          original.date_time_of_incident = date.toISOString().slice(0, 16);
        }

        // Check if primary contact index has changed
        const originalPrimaryIndex = original.incident_report_customer_contacts ? 
          original.incident_report_customer_contacts.findIndex(contact => contact.primary === true) : -1;
        const currentPrimaryIndex = this.primaryContactIndex;
        
        const primaryContactChanged = originalPrimaryIndex !== currentPrimaryIndex;

        // Check if primary pax index has changed
        const originalPrimaryPaxIndex = original.incident_report_affected_paxs ? 
          original.incident_report_affected_paxs.findIndex(pax => pax.primary === true) : -1;
        const currentPrimaryPaxIndex = this.primaryPaxIndex;
        
        const primaryPaxChanged = originalPrimaryPaxIndex !== currentPrimaryPaxIndex;
        
        return JSON.stringify(original) !== JSON.stringify(current) || primaryContactChanged || primaryPaxChanged;
      }
    },

    agentsWithDisplayName() {
      return this.agents.map(agent => ({
        ...agent,
        displayName: agent.email ? `${agent.name} (${agent.email})` : agent.name
      }));
    },
  },
  created() {
    // Load agents when component is created
    this.$store.dispatch('ticketViewer/loadAgents');
  },
  methods: {
    openModal(readOnly = false) {
      this.readOnly = readOnly;
      this.$bvModal.show("incident-report-modal");
      this.loadIncidentReportData();
    },

    loadIncidentReportData() {
      if (this.incidentReport) {
        // Load existing incident report data
        this.incidentReportLocal = { ...this.incidentReport };

        // Format datetime for input
        if (this.incidentReportLocal.date_time_of_incident) {
          const date = new Date(this.incidentReportLocal.date_time_of_incident);
          // Format as YYYY-MM-DDTHH:MM without timezone conversion
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          this.incidentReportLocal.date_time_of_incident = `${year}-${month}-${day}T${hours}:${minutes}`;
        }

        // Set primary contact index if customer contacts exist
        if (this.incidentReportLocal.incident_report_customer_contacts && 
            this.incidentReportLocal.incident_report_customer_contacts.length > 0) {
          const primaryIndex = this.incidentReportLocal.incident_report_customer_contacts.findIndex(
            contact => contact.primary === true
          );
          this.primaryContactIndex = primaryIndex >= 0 ? primaryIndex : null;
          
          // Initialize all contacts as expanded
          this.expandedContacts = this.incidentReportLocal.incident_report_customer_contacts.map((_, index) => index);
        } else {
          this.primaryContactIndex = null;
          this.expandedContacts = [];
        }

        // Set primary pax index if affected pax exist
        if (this.incidentReportLocal.incident_report_affected_paxs && 
            this.incidentReportLocal.incident_report_affected_paxs.length > 0) {
          const primaryPaxIndex = this.incidentReportLocal.incident_report_affected_paxs.findIndex(
            pax => pax.primary === true
          );
          this.primaryPaxIndex = primaryPaxIndex >= 0 ? primaryPaxIndex : null;
          
          // Initialize all affected pax as expanded
          this.expandedPaxs = this.incidentReportLocal.incident_report_affected_paxs.map((_, index) => index);
        } else {
          this.primaryPaxIndex = null;
          this.expandedPaxs = [];
        }
      } else {
        // Initialize with blank values
        this.resetForm();
        
        // Auto-populate booking number with ticket's axum booking ID if available
        if (this.ticket && this.ticket.axum_booking_id) {
          this.incidentReportLocal.booking_number = this.ticket.axum_booking_id;
        }
      }
    },

    resetForm() {
      this.incidentReportLocal = {
        type: "",
        itg_included_activity: null,
        travel_consultant_name: "",
        travel_consultant_id: null,
        stl_name: "",
        booking_number: "",
        tour_name: "",
        tour_leader: "",
        is_solo_pax: false,
        travel_agency_name: "",
        travel_agency_contact_name: "",
        travel_agency_contact_email: "",
        travel_agency_contact_phone: "",
        incident_report_customer_contacts: [],
        incident_report_affected_paxs: [],
        doctor_or_hospital_details: "",
        travel_insurance_company: "",
        travel_insurance_policy_number: "",
        travel_insurance_emergency_phone_number: "",
        date_time_of_incident: "",
        location_of_incident: "",
        was_insider_present: false,
        what_did_insider_see_do: "",
        description_of_incident: "",
        action_taken: "",
        relevant_safety_briefings: "",
        suggested_action_to_avoid_recurrence: "",
        declare_facts_true: false,
        declared_by_name: "",
      };
      this.primaryContactIndex = null;
      this.primaryPaxIndex = null;
      this.expandedContacts = [];
      this.expandedPaxs = [];
      this.serverErrors = [];
    },

    async saveIncidentReport() {
      // Validate required fields
      const validationErrors = this.validateForm();
      if (validationErrors.length > 0) {
        this.serverErrors = validationErrors;
        return;
      }

      this.isSaving = true;
      this.serverErrors = [];

      try {
        // Prepare data for submission
        const submitData = { ...this.incidentReportLocal };

        const response = await axios.put(
          API_BASE + "/tickets/" + this.ticket.id + "/incident-report",
          submitData
        );

        // Reset loading state
        this.isSaving = false;

        // Close modal and refresh data
        this.$bvModal.hide("incident-report-modal");
        this.$emit("incident-report-saved", response.data);

        // Show success message
        this.$bvToast.toast("Incident report saved successfully", {
          title: "Success",
          variant: "success",
          solid: true,
        });
      } catch (error) {
        this.isSaving = false;

        if (
          error.response &&
          error.response.data &&
          error.response.data.errors
        ) {
          this.serverErrors = this.flattenErrors(error.response.data.errors);
        } else {
          this.serverErrors = [
            "An error occurred while saving the incident report. Please try again.",
          ];
        }
      }
    },

    validateForm() {
      const errors = [];

      // No required field validations - all fields are optional

      return errors;
    },

    confirmClose() {
      if (!this.hasUnsavedChanges) {
        this.$bvModal.hide("incident-report-modal");
        this.resetForm();
        return;
      }

      this.$bvModal
        .msgBoxConfirm(
          "Are you sure you want to close? Any unsaved changes will be lost.",
          {
            title: "Confirm Close",
            size: "md",
            buttonSize: "md",
            okVariant: "danger",
            okTitle: "Yes, Close",
            cancelTitle: "Cancel",
            footerClass: "p-2",
            hideHeaderClose: true,
            centered: true,
          }
        )
        .then((value) => {
          if (value) {
            this.$bvModal.hide("incident-report-modal");
            this.resetForm();
          }
        });
    },

    handleBeforeClose(bvModalEvent) {
      if (this.readOnly) {
        // In read-only mode, no need to confirm close since no changes can be made
        return;
      }
      
      if (this.hasUnsavedChanges) {
        bvModalEvent.preventDefault();
        this.confirmClose();
      }
    },

    flattenErrors(errors) {
      if (typeof errors === "object") {
        return Object.values(errors).flat();
      }
      return [errors];
    },

    previousTab() {
      if (this.currentTabIndex > 0) {
        this.currentTabIndex--;
      }
    },

    nextTab() {
      if (this.currentTabIndex < this.totalTabs - 1) {
        // Validate current tab before proceeding
        const currentTabErrors = this.validateCurrentTab();
        if (currentTabErrors.length > 0) {
          this.serverErrors = currentTabErrors;
          return;
        }

        this.serverErrors = [];
        this.currentTabIndex++;
      }
    },

    validateCurrentTab() {
      const errors = [];

      // No required field validations - all fields are optional

      return errors;
    },

    setTab(index) {
      this.currentTabIndex = index;
    },

    addCustomerContact() {
      if (!this.incidentReportLocal.incident_report_customer_contacts) {
        this.incidentReportLocal.incident_report_customer_contacts = [];
      }
      
      const newContact = {
        name: "",
        relationship_to_affected_pax: "",
        email: "",
        mobile: "",
        notes: "",
        primary: false
      };
      
      this.incidentReportLocal.incident_report_customer_contacts.push(newContact);
      
      // Add new contact to expanded list
      const newIndex = this.incidentReportLocal.incident_report_customer_contacts.length - 1;
      this.expandedContacts.push(newIndex);
      
      // If this is the first contact, make it primary
      if (this.incidentReportLocal.incident_report_customer_contacts.length === 1) {
        this.setPrimaryContact(0);
      }
    },

    removeCustomerContact(index) {
      if (this.incidentReportLocal.incident_report_customer_contacts[index].primary) {
        this.primaryContactIndex = null;
      }
      
      this.incidentReportLocal.incident_report_customer_contacts.splice(index, 1);
      
      // Remove from expanded contacts and update indices
      this.expandedContacts = this.expandedContacts
        .filter(i => i !== index)
        .map(i => i > index ? i - 1 : i);
      
      // Update primary contact index if needed
      if (this.primaryContactIndex !== null && this.primaryContactIndex >= index) {
        this.primaryContactIndex = Math.max(0, this.primaryContactIndex - 1);
      }
    },

    setPrimaryContact(index) {
      // Remove primary flag from all contacts
      this.incidentReportLocal.incident_report_customer_contacts.forEach(contact => {
        contact.primary = false;
      });
      
      // Set primary flag for selected contact
      this.incidentReportLocal.incident_report_customer_contacts[index].primary = true;
      this.primaryContactIndex = index;
    },

    confirmDeleteContact(index) {
      this.$bvModal
        .msgBoxConfirm(
          "Are you sure you want to delete this contact?",
          {
            title: "Confirm Delete",
            size: "md",
            buttonSize: "md",
            okVariant: "danger",
            okTitle: "Yes, Delete",
            cancelTitle: "Cancel",
            footerClass: "p-2",
            hideHeaderClose: true,
            centered: true,
          }
        )
        .then((value) => {
          if (value) {
            this.removeCustomerContact(index);
          }
        });
    },

    toggleContactExpanded(index) {
      if (this.expandedContacts.includes(index)) {
        this.expandedContacts = this.expandedContacts.filter(i => i !== index);
      } else {
        this.expandedContacts.push(index);
      }
    },

    isContactExpanded(index) {
      if (this.readOnly) {
        return true;
      }

      return this.expandedContacts.includes(index);
    },

    addAffectedPax() {
      if (!this.incidentReportLocal.incident_report_affected_paxs) {
        this.incidentReportLocal.incident_report_affected_paxs = [];
      }
      
      const newPax = {
        name: "",
        date_of_birth: "",
        email: "",
        mobile: "",
        primary: false
      };
      
      this.incidentReportLocal.incident_report_affected_paxs.push(newPax);
      
      // Add new pax to expanded list
      const newIndex = this.incidentReportLocal.incident_report_affected_paxs.length - 1;
      this.expandedPaxs.push(newIndex);
      
      // If this is the first pax, make it primary
      if (this.incidentReportLocal.incident_report_affected_paxs.length === 1) {
        this.setPrimaryPax(0);
      }
    },

    removeAffectedPax(index) {
      if (this.incidentReportLocal.incident_report_affected_paxs[index].primary) {
        this.primaryPaxIndex = null;
      }
      
      this.incidentReportLocal.incident_report_affected_paxs.splice(index, 1);
      
      // Remove from expanded pax and update indices
      this.expandedPaxs = this.expandedPaxs
        .filter(i => i !== index)
        .map(i => i > index ? i - 1 : i);
      
      // Update primary pax index if needed
      if (this.primaryPaxIndex !== null && this.primaryPaxIndex >= index) {
        this.primaryPaxIndex = Math.max(0, this.primaryPaxIndex - 1);
      }
    },

    setPrimaryPax(index) {
      // Remove primary flag from all pax
      this.incidentReportLocal.incident_report_affected_paxs.forEach(pax => {
        pax.primary = false;
      });
      
      // Set primary flag for selected pax
      this.incidentReportLocal.incident_report_affected_paxs[index].primary = true;
      this.primaryPaxIndex = index;
    },

    confirmDeletePax(index) {
      this.$bvModal
        .msgBoxConfirm(
          "Are you sure you want to delete this affected PAX?",
          {
            title: "Confirm Delete",
            size: "md",
            buttonSize: "md",
            okVariant: "danger",
            okTitle: "Yes, Delete",
            cancelTitle: "Cancel",
            footerClass: "p-2",
            hideHeaderClose: true,
            centered: true,
          }
        )
        .then((value) => {
          if (value) {
            this.removeAffectedPax(index);
          }
        });
    },

    togglePaxExpanded(index) {
      if (this.expandedPaxs.includes(index)) {
        this.expandedPaxs = this.expandedPaxs.filter(i => i !== index);
      } else {
        this.expandedPaxs.push(index);
      }
    },

    isPaxExpanded(index) {
      if (this.readOnly) {
        return true;
      }

      return this.expandedPaxs.includes(index);
    },

    togglePolicyAccordion() {
      this.policyAccordionOpen = !this.policyAccordionOpen;
    },

    onTravelConsultantChange(selectedAgent) {
      if (selectedAgent) {
        // Find the selected agent and update the name
        const agent = this.agents.find(a => a.id === selectedAgent);
        if (agent) {
          this.incidentReportLocal.travel_consultant_name = agent.name;
        }
      } else {
        // Clear the name if no agent is selected
        this.incidentReportLocal.travel_consultant_name = '';
      }
    },
  },
};
</script>

<style lang="scss">
#incident-report-modal {
  .incident-report-form {
    overflow-y: auto;
    
    .form-group {
      label {
        font-weight: 600;
        color: #495057;
        margin-bottom: 0.5rem;
      }

      .form-control {
        border-radius: 0.5rem;
        border: 1px solid #ced4da;
        padding: 0.75rem 1rem;
        // font-size: 0.95rem;
        height: auto;
        // transition: border-color .15s ease-in-out,box-shadow .15s ease-in-out;

        &:focus {
          border-color: #2a3042;
          //   box-shadow: 0 0 0 0.2rem rgba(42, 48, 66, 0.25);
        }
      }

      .form-check-input:checked {
        background-color: #2a3042;
        border-color: #2a3042;
      }
    }
  }

  .toolbar {
    border-top: 1px #ebeff3 solid !important;
    border-bottom: 1px #cfd7df solid !important;
    padding-top: 11px;
    padding-bottom: 11px;
    box-shadow: 0 2px 4px 0 rgba(24, 50, 71, 0.08);
    z-index: 1;

    .btn-group {
      .btn {
        border-radius: 0.375rem;
        margin-right: 0.5rem;

        &:last-child {
          margin-right: 0;
        }
      }
    }
  }

  .modal-dialog {
    max-width: 100vw;
    width: calc(100vw);
    margin: auto;
    transform: none;

    .modal-body {
      padding: 0;
      display: flex;
      flex-direction: column;
      height: 100%;
      max-height: calc(100vh - 127px);

      .tab-content {
        flex: 1;
        overflow-y: auto;
      }

    //   .modal-footer {
        // flex-shrink: 0;
    //   }
    }

    .modal-header {
      border-bottom: none;
      background: #2a3042;
      padding-top: 1rem;
      padding-bottom: 1rem;
      padding-left: 1.2rem;
      padding-right: 1.2rem;

      .modal-title {
        color: #fff !important;
      }

      .close {
        // hide the close button
        // display: none;
        // make it not clickable
        // pointer-events: none;
        filter: invert(1);
        padding-top: 18px;
        padding-right: 20px;
      }
    }

    .modal-content {
      border: 0;
      min-height: calc(100vh - 300px);
      height: calc(100vh);
      max-height: calc(100vh);
    }
  }

  .modal-footer {
    border-top: 1px solid #dee2e6;
    padding-left: 1rem;
    padding-right: 1rem;
  }

  // Footer button styling
  .modal-footer .btn {
    min-width: 100px;
  }

  // Customer contacts styling
  .card {
    border: 1px solid #dee2e6;
    border-radius: 0.5rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    .card-header {
      background-color: #f8f9fa;
      border-bottom: 1px solid #dee2e6;
      padding: 0.75rem 1rem;
      border-radius: 0.5rem 0.5rem 0 0;

      h6 {
        margin: 0;
        font-weight: 600;
        color: #495057;
      }

      .btn-link {
        color: #6c757d;
        text-decoration: none;
        transition: color 0.15s ease-in-out;

        &:hover {
          color: #495057;
        }

        &:focus {
          box-shadow: none;
        }

        i {
          font-size: 0.875rem;
          transition: transform 0.15s ease-in-out;
        }
      }
    }

    .card-body {
      padding: 1rem;
    }
  }

  .collapsed-card-header {
    border-bottom: 0 !important;
    border-radius: 0.5rem !important;
  }

  .form-check {
    margin-bottom: 0;
    
    .form-check-input {
      margin-right: 0.5rem;
    }
    
    .form-check-label {
      font-size: 0.875rem;
      color: #6c757d;
      margin-bottom: 0;
    }
  }

  .btn-sm {
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
  }

  .gap-2 {
    gap: 0.5rem;
  }

  // Policy accordion styling
  .policy-accordion {
    border: 1px solid #e3e6f0;
    border-radius: 0.5rem;
    background: linear-gradient(135deg, #f8f9fc 0%, #eaecf4 100%);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease-in-out;

    &.expanded {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      border-color: #d1d3e2;
    }

    .policy-header {
      padding: 1rem 1.25rem;
      cursor: pointer;
      border-bottom: 1px solid transparent;
      transition: all 0.2s ease-in-out;

      &:hover {
        background: rgba(42, 48, 66, 0.04);
      }

      h6 {
        color: #2a3042;
        font-weight: 600;
        margin: 0;

        i {
          color: #4e73df;
        }
      }

      .fa-chevron-down,
      .fa-chevron-up {
        color: #6c757d;
        transition: transform 0.2s ease-in-out;
      }
    }

    .policy-content {
      padding: 1.25rem;
      background: rgba(255, 255, 255, 0.7);
      border-top: 1px solid #e3e6f0;
      border-radius: 0 0 0.5rem 0.5rem;

      p {
        color: #495057;
        line-height: 1.6;
        margin-bottom: 0.75rem;

        &:last-child {
          margin-bottom: 0;
        }
      }

      ul {
        color: #495057;
        line-height: 1.6;
        margin-bottom: 0.75rem;
        padding-left: 1.25rem;

        li {
          margin-bottom: 0.25rem;
        }
      }

      strong {
        color: #2a3042;
      }
    }
  }
}
</style>